import { EnvironmentService, Environment } from './enviroment.service';
import { Observable, of } from 'rxjs'; // of 用於回退
import { switchMap } from 'rxjs/operators';

// 這個 Proxy 將動態地從 EnvironmentService 的單一實例中獲取環境數據
export const environment: Environment = new Proxy({} as Environment, {
  get: (target, prop: string | symbol, receiver) => {
    if (EnvironmentService.instance) {
      const currentEnv = EnvironmentService.instance.environment; // 訪問 getter
      if (currentEnv && typeof prop === 'string') {
        return currentEnv[prop];
      }
    }
    // 如果 EnvironmentService.instance 尚未就緒或屬性不存在於其 environment 物件中
    // 這裡返回 undefined 是標準行為。
    // 也可以考慮返回一個預設環境的屬性值，但這會使 Proxy 行為複雜化。
    // console.warn(`global-environment: Proxy access for '${String(prop)}' before EnvironmentService.instance is ready or property not found.`);
    return undefined;
  },
  has(target, key) {
    if (EnvironmentService.instance && EnvironmentService.instance.environment) {
      return key in EnvironmentService.instance.environment;
    }
    return false;
  },
  ownKeys(target) {
    if (EnvironmentService.instance && EnvironmentService.instance.environment) {
      return Reflect.ownKeys(EnvironmentService.instance.environment);
    }
    return [];
  },
  getOwnPropertyDescriptor(target, key) {
    if (EnvironmentService.instance && EnvironmentService.instance.environment) {
      return Reflect.getOwnPropertyDescriptor(EnvironmentService.instance.environment, key);
    }
    return undefined;
  }
}) as Environment;

// environment$ 也應該來自 EnvironmentService 的單一實例的 environment$
// 這確保了訂閱者獲得與 Proxy 使用者相同的數據源和更新。
export const environment$: Observable<Environment | null> = new Observable(subscriber => {
  // 檢查 EnvironmentService.instance 是否已存在
  if (EnvironmentService.instance && EnvironmentService.instance.environment$) {
    return EnvironmentService.instance.environment$.subscribe(subscriber);
  } else {
    // 如果實例尚不存在 (理論上 APP_INITIALIZER 應該確保它在主要應用邏輯運行前存在)
    // 我們可以創建一個延遲的訂閱，或者立即發出一個初始的 null/預設值然後完成。
    // 為了簡單和安全，如果 instance 不存在，我們發出 null。
    // 更好的做法是依賴 APP_INITIALIZER 確保 instance 總是可用的。
    console.warn('global-environment: EnvironmentService.instance not available at time of environment$ subscription. Emitting null or default.');
    // 為了讓下游組件不至於因 null 而出錯，可以考慮發出一個預設環境
    // 但這又回到了在 global-environment.ts 中管理預設值的問題。
    // 最簡單的回退是 of(null)
    // 或者，如果 EnvironmentService 有一個靜態的 getDefaultEnvironment，可以使用它：
    // return of(EnvironmentService.getDefaultEnvironmentStatic()).subscribe(subscriber);
    // 暫時使用 of(null)
    return of(null).subscribe(subscriber);
  }
});

// 注意：
// 1. EnvironmentService 必須在 app.config.ts (或 app.module.ts) 中作為常規服務提供，
//    以便 Angular DI 系統創建它，並且 APP_INITIALIZER 使用的是這個 DI 創建的實例。
// 2. APP_INITIALIZER 中的工廠函數 (initializeAppFactory) 必須正確地等待
//    EnvironmentService.loadEnvironmentConfig() 的 Promise 完成。
